import { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { 
  ArrowLeft, 
  Users, 
  MapPin, 
  Calendar,
  Mail,
  ExternalLink,
  Shield,
  Building2,
  Crown
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { clubsApi } from '../lib/api/clubs';
import TopBar from '../components/TopBar';
import Button from '../components/Button';
import { DashboardSkeleton } from '../components';

export default function PlatformAdminClubDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { me } = useAuth();

  // Redirect non-platform-admins
  useEffect(() => {
    if (me && !me.is_platform_admin) {
      navigate('/');
    }
  }, [me, navigate]);

  // Fetch club details for platform admin
  const { data: clubData, isLoading, error } = useQuery({
    queryKey: ['admin', 'club', slug],
    queryFn: () => clubsApi.getClubForAdmin(slug!),
    enabled: !!slug && !!me?.is_platform_admin,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-oro-bg">
        <TopBar title="Platform Admin" />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <DashboardSkeleton />
        </div>
      </div>
    );
  }

  if (error || !clubData) {
    return (
      <div className="min-h-screen bg-oro-bg">
        <TopBar title="Platform Admin" />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-text-primary mb-4">Club Not Found</h1>
            <p className="text-text-secondary mb-6">The requested club could not be found.</p>
            <Button onClick={() => navigate('/platform-admin/home')}>
              Back to Admin Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const { club, owner } = clubData;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long', 
      day: 'numeric'
    });
  };

  const getVisibilityBadge = (visibility: string) => {
    const colors = {
      public: 'bg-green-100 text-green-800',
      invite_only: 'bg-yellow-100 text-yellow-800',
      private: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[visibility as keyof typeof colors] || 'bg-oro-surface-alt text-text-secondary'}`}>
        {visibility.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-oro-bg">
      <TopBar title="Platform Admin" />
      
      {/* Header */}
      <div className="bg-oro-surface border-b border-oro-border">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/platform-admin/home')}
                className="p-2"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <div className="flex items-center space-x-3">
                  <h1 className="text-2xl font-bold text-text-primary">{club.name}</h1>
                  {getVisibilityBadge(club.visibility)}
                </div>
                <div className="flex items-center space-x-2 text-sm text-text-secondary">
                  <Shield className="w-4 h-4" />
                  <span>Platform Admin View</span>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Link 
                to={`/clubs/${club.slug}`}
                className="inline-flex items-center px-3 py-2 border border-oro-border shadow-sm text-sm leading-4 font-medium rounded-md text-text-secondary bg-oro-surface hover:bg-oro-surface-alt"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                View Public Page
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Club Information */}
            <div className="bg-oro-surface rounded-lg shadow p-6">
              <h2 className="text-lg font-medium text-text-primary mb-4">Club Information</h2>
              
              {club.hero_image_url && (
                <div className="mb-4">
                  <img 
                    src={club.hero_image_url} 
                    alt={`${club.name} hero image`}
                    className="w-full h-48 object-cover rounded-lg"
                  />
                </div>
              )}
              
              <div className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-text-secondary">Description</dt>
                  <dd className="mt-1 text-sm text-text-primary">
                    {club.description || 'No description provided.'}
                  </dd>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-text-secondary flex items-center">
                      <MapPin className="w-4 h-4 mr-1" />
                      Location
                    </dt>
                    <dd className="mt-1 text-sm text-text-primary">{club.city}, {club.state}</dd>
                  </div>
                  
                  <div>
                    <dt className="text-sm font-medium text-text-secondary flex items-center">
                      <Users className="w-4 h-4 mr-1" />
                      Members
                    </dt>
                    <dd className="mt-1 text-sm text-text-primary">{club.member_count}</dd>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-text-secondary flex items-center">
                      <Building2 className="w-4 h-4 mr-1" />
                      Subscription Level
                    </dt>
                    <dd className="mt-1 text-sm text-text-primary">{club.subscription_level}</dd>
                  </div>
                  
                  <div>
                    <dt className="text-sm font-medium text-text-secondary flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      Created
                    </dt>
                    <dd className="mt-1 text-sm text-text-primary">{formatDate(club.created_at)}</dd>
                  </div>
                </div>
              </div>
            </div>

            {/* Platform Admin Actions */}
            <div className="bg-oro-surface rounded-lg shadow p-6">
              <h2 className="text-lg font-medium text-text-primary mb-4">Platform Admin Actions</h2>
              <div className="space-y-3">
                
                <Button
                  variant="secondary"
                  onClick={() => navigate(`/admin/invites?club=${club.slug}`)}
                  className="w-full justify-start"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Create Club Leader Invite
                </Button>
                
                {owner && (
                  <Button
                    variant="secondary"
                    onClick={() => window.open(`mailto:${owner.email}?subject=ORO Platform - ${club.name}`, '_blank')}
                    className="w-full justify-start"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Contact Club Owner
                  </Button>
                )}
                
                <Button
                  variant="secondary"
                  onClick={() => window.open(`/clubs/${club.slug}`, '_blank')}
                  className="w-full justify-start"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  View Public Club Page
                </Button>
                
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            
            {/* Club Owner */}
            {owner && (
              <div className="bg-oro-surface rounded-lg shadow p-6">
                <h2 className="text-lg font-medium text-text-primary mb-4 flex items-center">
                  <Crown className="w-5 h-5 mr-2 text-yellow-600" />
                  Club Owner
                </h2>
                <div className="space-y-3">
                  <div>
                    <dt className="text-sm font-medium text-text-secondary">Name</dt>
                    <dd className="mt-1 text-sm text-text-primary">{owner.public_name}</dd>
                  </div>
                  
                  <div>
                    <dt className="text-sm font-medium text-text-secondary">Email</dt>
                    <dd className="mt-1 text-sm text-text-primary">
                      <a 
                        href={`mailto:${owner.email}`}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        {owner.email}
                      </a>
                    </dd>
                  </div>
                  
                  {owner.location && (
                    <div>
                      <dt className="text-sm font-medium text-text-secondary">Location</dt>
                      <dd className="mt-1 text-sm text-text-primary">{owner.location}</dd>
                    </div>
                  )}
                  
                  <div>
                    <dt className="text-sm font-medium text-text-secondary">Member Since</dt>
                    <dd className="mt-1 text-sm text-text-primary">{formatDate(owner.created_at)}</dd>
                  </div>
                  
                  <div className="pt-3 border-t border-oro-border">
                    <Link 
                      to={`/u/${owner.id}`}
                      className="inline-flex items-center text-sm text-blue-600 hover:text-blue-700"
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      View Public Profile
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {/* Privacy Notice */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <Shield className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-medium text-blue-900">Privacy Notice</h3>
                  <p className="text-sm text-blue-700 mt-1">
                    As a platform admin, you can see club metadata and owner information, 
                    but cannot access member lists, events, or chat without joining the club.
                  </p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}